/**
 * Configuration Module - Admin
 * 
 * Centralized configuration for React Admin application.
 * Loads environment variables and provides typed configuration.
 * 
 * @version 2.0.0
 */

/**
 * Configuration object
 */
const config = {
  // Application Info
  app: {
    name: process.env.REACT_APP_APP_NAME || 'Roots & Wings Admin',
    version: process.env.REACT_APP_APP_VERSION || '2.0.0',
  },

  // Environment
  environment: process.env.NODE_ENV || 'development',
  isDevelopment: (process.env.NODE_ENV || 'development') === 'development',
  isProduction: process.env.NODE_ENV === 'production',

  // API Configuration
  API_BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://api.rwfngo.com/api',
  api: {
    baseUrl: process.env.REACT_APP_API_BASE_URL || 'https://api.rwfngo.com/api',
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || 30000, 10),
  },

  // Image / uploads base URL (no trailing slash). Used for campaign images, documents, gallery media.
  imageBaseUrl: process.env.REACT_APP_IMAGE_URL || (() => {
    const api = process.env.REACT_APP_API_BASE_URL || 'https://api.rwfngo.com/api';
    return api.replace(/\/api\/?$/, '') || api;
  })(),

  // Authentication
  auth: {
    tokenKey: 'adminToken',
    refreshInterval: parseInt(process.env.REACT_APP_TOKEN_REFRESH_INTERVAL || 3600000, 10), // 1 hour
    tokenExpiry: process.env.REACT_APP_TOKEN_EXPIRY || '24h',
  },

  // Admin Settings
  admin: {
    itemsPerPage: parseInt(process.env.REACT_APP_ITEMS_PER_PAGE || 10, 10),
    maxFileUploadSize: parseInt(process.env.REACT_APP_MAX_FILE_UPLOAD_SIZE || 5242880, 10), // 5MB
  },

  // Feature Flags
  features: {
    analytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    errorReporting: process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true',
  },

  // Contact
  contact: {
    supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || 'support@rootsandwings.org',
  },

  // Logging
  logging: {
    level: process.env.REACT_APP_LOG_LEVEL || 'debug',
  },
};

// Validate configuration
if (!config.api.baseUrl) {
  console.error('API Base URL not configured');
}

/** Resolve full URL for images/uploads. Uses REACT_APP_IMAGE_URL as base. Normalizes localhost URLs from API to env base. */
export function getAssetUrl(pathOrUrl) {
  if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';
  const s = pathOrUrl.trim();
  if (s.startsWith('data:') || s.startsWith('blob:')) return s;
  const base = config.imageBaseUrl || '';
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      const host = u.hostname.toLowerCase();
      if (host === 'localhost' || host === '127.0.0.1') {
        if (!base) return s;
        return base.replace(/\/$/, '') + (u.pathname.startsWith('/') ? u.pathname : '/' + u.pathname);
      }
    } catch (_) {}
    return s;
  }
  if (!base) return s;
  return base.replace(/\/$/, '') + (s.startsWith('/') ? s : '/' + s);
}

export default config;
